/**
 * 版片面料独立修改工具
 *
 * 当用户选择"否（只修改当前版片）"时：
 * 1. 为当前版片分配一个未被使用的新 materialIndex
 * 2. 把同一面料 ID 写入路由参数（id1/id2）
 * 3. 在 query.overrides 中记录 { piece: newMaterialIndex } 映射
 *    加载时用此映射覆盖 relation.json 里的 materialIndex
 *
 * 注意：用 shape.piece（版片名称）作为 key，因为 shape.id 在
 * handle3DResource 阶段还是空字符串，piece 才是稳定的唯一标识。
 */

import type { Router } from 'vue-router';
import type { Shape } from '@repo/style3d';

/** 路由最多支持的 materialIndex 数量（id/id1/id2） */
const MAX_MATERIAL_INDEX = 2;

/**
 * 找到一个未被 shapeList 使用的 materialIndex
 * 返回 null 表示已达上限，无法再分配
 */
export function allocateNewMaterialIndex(shapeList: Shape[]): number | null {
  const usedIndices = new Set(shapeList.map(s => s.materialIndex ?? 0));
  for (let i = 0; i <= MAX_MATERIAL_INDEX; i++) {
    if (!usedIndices.has(i)) return i;
  }
  return null;
}

/**
 * 解析路由 query 中的 overrides 参数
 * 格式：{ [piece]: materialIndex }
 */
export function parseOverrides(overridesParam: string | undefined): Record<string, number> {
  if (!overridesParam) return {};
  try {
    return JSON.parse(decodeURIComponent(overridesParam));
  } catch {
    return {};
  }
}

/**
 * 将 overrides 序列化为路由 query 参数
 */
export function serializeOverrides(overrides: Record<string, number>): string {
  return encodeURIComponent(JSON.stringify(overrides));
}

/**
 * 为当前版片分配新 materialIndex，并更新路由参数和 overrides
 *
 * @param piece         当前版片的 piece 名称（稳定唯一标识）
 * @param currentMaterialIndex  当前版片的 materialIndex
 * @param shapeList     所有版片列表（用于查找未使用的 index）
 * @param router        vue-router 实例
 * @param fabricId      当前面料 ID（写入新 materialIndex 对应的路由参数）
 * @returns 分配到的新 materialIndex，null 表示已达上限
 */
export async function assignNewMaterialIndex(
  piece: string,
  currentMaterialIndex: number,
  shapeList: Shape[],
  router: Router,
  fabricId: string
): Promise<number | null> {
  const newIndex = allocateNewMaterialIndex(shapeList);
  if (newIndex === null) {
    console.warn('[materialOverride] 已达 materialIndex 上限，无法独立修改');
    return null;
  }

  const route = router.currentRoute.value;
  const params = { ...route.params } as Record<string, string>;
  const query = { ...route.query } as Record<string, string>;

  // 把面料 ID 写入新 materialIndex 对应的路由参数
  const paramKey = newIndex === 0 ? 'id' : `id${newIndex}`;
  params[paramKey] = fabricId;

  // 更新 overrides：记录 piece -> newMaterialIndex
  const overrides = parseOverrides(query.overrides);
  overrides[piece] = newIndex;
  query.overrides = serializeOverrides(overrides);

  // 同步把原 materialIndex 的颜色复制到新 materialIndex（如果有）
  if (query.colors) {
    try {
      const colorsMap: Record<number, unknown> = JSON.parse(decodeURIComponent(query.colors));
      if (colorsMap[currentMaterialIndex] !== undefined) {
        colorsMap[newIndex] = colorsMap[currentMaterialIndex];
        query.colors = encodeURIComponent(JSON.stringify(colorsMap));
      }
    } catch {
      // ignore
    }
  }

  await router.replace({ params, query });
  return newIndex;
}
